import { useId, useMemo, useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { DefinitionProcess, JsonObject, Problem, Run } from '../api/types';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ProblemNotice } from '../components/ProblemNotice';
import { groupIssuesByKey } from '../forms/schema';
import { FieldRow, type WidgetValue } from '../forms/StandardForm';
import {
  checkStartInput,
  planStartInput,
  widgetValuesFromInput,
  type DeclaredInputSchema,
} from '../forms/startInput';
import { usePlatform } from '../app/platform';
import { toProblem } from '../app/useAsync';

export interface StartRunPanelProps {
  workspaceId: string;
  appId: string;
  process: DefinitionProcess;
  /**
   * processが宣言した開始入力のschema。宣言が無ければnullで、従来どおりJSON入力欄だけを出す。
   * schemaがフォームに収まる形なら入力フォームを生成する（forms/startInput.ts）。
   */
  inputSchema: DeclaredInputSchema | null;
  /** provisioning/updating/degraded の間は開始させない。 */
  disabled: boolean;
  onStarted(run: Run): void;
}

/**
 * 専用のclient UIが無いmicroappでも、BPMNの開始イベントからプロセスを起動できるようにする。
 * Timer開始は基盤が時刻で起こすものなので、ここからは開始しない。
 */
export function StartRunPanel({
  workspaceId,
  appId,
  process,
  inputSchema,
  disabled,
  onStarted,
}: StartRunPanelProps) {
  const { api } = usePlatform();
  const idPrefix = useId();
  const noneStarts = process.startEvents.filter((event) => event.type === 'none');
  const messageStarts = process.startEvents.filter(
    (event) => event.type === 'message' && event.messageName,
  );
  const timerStarts = process.startEvents.filter((event) => event.type === 'timer');

  const [messageName, setMessageName] = useState(messageStarts[0]?.messageName ?? '');
  const [body, setBody] = useState('{}');
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [accepted, setAccepted] = useState<Run | null>(null);
  // 同じ入力の再送で二重起動しないよう、開始が成立するまでキーを固定する。
  const [attempt, setAttempt] = useState(0);
  const idempotencyKey = useMemo(() => newIdempotencyKey(), [attempt]);

  // ここから下の状態は、inputSchemaからフォームを組めたときだけ使う。
  // Message開始のpayloadも同じprocessの開始入力なので、同じフォームで組む。
  const plan = inputSchema === null ? null : planStartInput(inputSchema);
  const form = plan?.kind === 'form' ? plan : null;
  const [jsonMode, setJsonMode] = useState(false);
  const [edited, setEdited] = useState<Record<string, WidgetValue>>({});
  // エラーは触った項目から出す。触っていない項目のエラーは、開始を押したときにまとめて出す。
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [attempted, setAttempted] = useState(false);
  // JSONからフォームへ戻したときに反映できなかったキー。黙って捨てずに知らせる。
  const [dropped, setDropped] = useState<string[]>([]);

  const formCheck = form !== null && !jsonMode ? checkStartInput(form, edited) : null;
  const visibleIssues =
    formCheck?.issues.filter((issue) => attempted || touched.has(issue.key)) ?? [];
  const issuesByKey = groupIssuesByKey(visibleIssues);

  const parsed = parseJsonObject(body);
  const inputReady = formCheck !== null ? visibleIssues.length === 0 : parsed.ok;
  const canStart =
    !disabled && !submitting && inputReady && (noneStarts.length > 0 || messageName !== '');

  const start = async (mode: 'none' | 'message') => {
    let input: JsonObject;
    if (formCheck !== null) {
      // まだ出していないエラーが残っていれば送らず、項目の近くへ出す。
      if (formCheck.issues.length > 0) {
        setAttempted(true);
        return;
      }
      input = formCheck.input;
    } else {
      if (!parsed.ok) return;
      input = parsed.value;
    }
    setSubmitting(true);
    setProblem(null);
    try {
      const run =
        mode === 'none'
          ? await api.startRun(
              workspaceId,
              appId,
              { processKey: process.key, input },
              { idempotencyKey },
            )
          : await api.startByMessage(
              workspaceId,
              appId,
              process.key,
              { messageName, payload: input },
              { idempotencyKey },
            );
      setAccepted(run);
      setAttempt((value) => value + 1);
      onStarted(run);
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setSubmitting(false);
    }
  };

  const switchToJson = () => {
    if (formCheck === null) return;
    // 今のフォームの値を、そのまま送られる形のJSONで見せる。
    setBody(JSON.stringify(formCheck.input, null, 2));
    setDropped([]);
    setJsonMode(true);
  };

  const switchToForm = () => {
    if (form === null || !parsed.ok) return;
    const reflected = widgetValuesFromInput(form, parsed.value);
    setEdited(reflected.values);
    setDropped(reflected.dropped);
    setJsonMode(false);
  };

  if (noneStarts.length === 0 && messageStarts.length === 0) {
    return (
      <Card title="このプロセスの開始">
        <p className="hint">
          {timerStarts.length > 0
            ? '時刻・周期による開始だけを持つプロセスです。画面からは開始しません。'
            : '画面から開始できる開始イベントがありません。'}
        </p>
      </Card>
    );
  }

  const modeToggle =
    form === null ? undefined : jsonMode ? (
      // 壊れたJSONからは戻さない。どこが読めないかは入力欄の下に出ている。
      <Button variant="text" disabled={!parsed.ok} onClick={switchToForm}>
        フォームで入力
      </Button>
    ) : (
      <Button variant="text" onClick={switchToJson}>
        JSONで入力
      </Button>
    );

  return (
    <Card title="このプロセスを開始する" actions={modeToggle}>
      {plan?.kind === 'json' && (
        <p className="hint start-input-note">
          開始入力のschemaをフォームにできないため、JSONで入力します（{plan.reason}）。
        </p>
      )}

      {form !== null && formCheck !== null ? (
        <div className="form start-input-form">
          {form.fields.length === 0 && <p className="hint">入力する項目はありません。</p>}
          {form.fields.map((entry, index) => {
            const key = entry.field.key;
            return (
              <FieldRow
                key={key}
                field={entry.field}
                id={`${idPrefix}-${index}`}
                value={formCheck.widgetValues[key] ?? ''}
                messages={issuesByKey.get(key) ?? []}
                disabled={submitting}
                required={entry.required}
                description={entry.description}
                range={entry.range}
                onChange={(next) => setEdited((prev) => ({ ...prev, [key]: next }))}
                onBlur={() => setTouched((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))}
              />
            );
          })}
          {dropped.length > 0 && (
            <p className="hint">
              フォームに無い項目・フォームで表せない値は外しました: {dropped.join(', ')}
            </p>
          )}
        </div>
      ) : (
        <label className="field">
          <span className="field-label">
            {noneStarts.length > 0 ? '開始入力（JSON object）' : 'payload（JSON object）'}
          </span>
          <textarea
            className="input mono"
            rows={form === null ? 3 : 8}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
          {!parsed.ok && <span className="field-error">{parsed.message}</span>}
        </label>
      )}

      {messageStarts.length > 0 && (
        <label className="field inline">
          <span className="field-label">message</span>
          <select
            className="input"
            value={messageName}
            onChange={(event) => setMessageName(event.target.value)}
          >
            {messageStarts.map((event) => (
              <option key={event.id} value={event.messageName}>
                {event.messageName}
              </option>
            ))}
          </select>
        </label>
      )}

      {/* サーバーがinputSchemaで弾いた入力。detailは英語のまま届くので、日本語の一言を添える。 */}
      {problem?.status === 422 && problem.code === 'invalid_input' && (
        <p className="failure start-input-rejected">
          入力が開始条件に合いません。下の詳細（サーバーの検証結果）を確かめて、入力を直してください。
        </p>
      )}
      <ProblemNotice problem={problem} tone={problem?.status === 503 ? 'notice' : 'error'} />

      <div className="row">
        {noneStarts.length > 0 && (
          <Button variant="primary" disabled={!canStart} onClick={() => void start('none')}>
            {submitting ? '送信中…' : '開始する'}
          </Button>
        )}
        {messageStarts.length > 0 && (
          <Button disabled={!canStart} onClick={() => void start('message')}>
            messageで開始する
          </Button>
        )}
        {disabled && <span className="hint">配備中またはdegradedのため開始を受け付けません。</span>}
      </div>

      {accepted && (
        <p className="notice">
          受付しました（run {accepted.id}）。202は受付であって処理の成功ではありません。
        </p>
      )}
    </Card>
  );
}

type ParsedBody = { ok: true; value: JsonObject } | { ok: false; message: string };

function parseJsonObject(text: string): ParsedBody {
  try {
    const value: unknown = JSON.parse(text);
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return { ok: false, message: 'JSON objectを入力してください。' };
    }
    return { ok: true, value: value as JsonObject };
  } catch {
    return { ok: false, message: 'JSONとして読めません。' };
  }
}
